import { avatar, darkLight, settingLight } from "@/assets";
import { remoteAddress } from "@/constants";

import { useTheme } from "@/hooks/useTheme";
import { PatchAppend, PatchJSON } from "@/lib/http";
import { cn, resolveImageUrl } from "@/lib/utils";
import { Loading } from "notiflix";
import { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { MdClose } from "react-icons/md";
import { TbCameraPlus } from "react-icons/tb";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import DeactivateAccount from "../tabs/DeactivateAccount";

const ProfileDetails = () => {
  const user = useSelector((state) => state.user.user);
  const [imgFile, setImgFile] = useState(null);
  const [toggle, setToggle] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const { toggleTheme, theme } = useTheme();

  const profile = {};

  const { handleSubmit, register } = useForm({
    defaultValues: {
      name: user?.name || "",
      username: user?.username || "",
      phone: user?.phone || "",
      email: user?.email || "",
      address: user?.address || "",
      bvn: user?.bvn || "",
      state: user?.state || "",
      country: user?.country || "",
      nin: user?.nin || "",
    },
  });

  const handleImageChange = useCallback((file) => {
    if (file) {
      if (file.type.startsWith("image")) {
        setPreviewImage(URL.createObjectURL(file));
      } else {
        toast.error("Please select a valid image file.");
        setImgFile(null);
      }
    }
  }, []);

  useEffect(() => {
    handleImageChange(imgFile);

    return () => {
      if (previewImage) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [imgFile, handleImageChange]);

  const uploadImage = async (e) => {
    e.preventDefault();

    if (imgFile && imgFile.size > 2 * 1024 * 1024) {
      toast.error("File size exceeds 2 MB. Please select a smaller file.");
      return;
    }

    if (imgFile) {
      Loading.standard("Uploading Image");
      const formDataToSend = new FormData();
      formDataToSend.append("image", imgFile);
      const { err } = await PatchAppend({
        url: `/api/user`,
        body: formDataToSend,
      });
      if (!err) {
        toast.success(
          "Updated successfully! refresh page to fully reflect changes",
        );
        setToggle(false);
        setImgFile(null); // Reset the image file
        setPreviewImage(null); // Reset the preview image
      } else {
        toast.error(err);
      }
      Loading.remove();
    }
  };

  const handleSave = async (body) => {
    Loading.standard("Saving User Info");
    const { err } = await PatchJSON({
      url: "/api/user",
      body,
    });
    if (!err) {
      toast.success("User Info Updated Successfully");
    } else {
      toast.error(err);
    }
    Loading.remove();
  };

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      <div
        className={`${toggle && "hidden"} dark:border-darkBorder rounded-xl border lg:w-[45%] lg:shadow-2xl`}
      >
        <h2 className="mt-3 ml-2 w-fit rounded-md bg-[#54d62c29] p-1 text-xs font-bold text-[#229a16]">
          Billzpay
        </h2>

        <div className={`flex w-full flex-col justify-center gap-5 py-[24px]`}>
          <div className="flex flex-col items-center">
            <img
              src={user.avatar ? resolveImageUrl(user.avatar) : avatar}
              alt="User Avatar"
              className="size-[144px] rounded-full object-cover"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = avatar;
              }}
            />
            <div className="pt-5">
              {/* {`/files/storage/images/users/${user.id}.webp`} */}
              <button
                onClick={() => setToggle(true)}
                className="bg-primary rounded-lg px-5 py-2 font-medium text-white"
              >
                Edit Picture
              </button>
            </div>

            <div className="flex flex-col gap-7">
              <div className="flex flex-row-reverse items-start gap-3 pt-2">
                <div title="Dark Mode">
                  <input
                    type="radio"
                    id="flexRadioDefault1"
                    checked={theme === "dark"}
                    onChange={toggleTheme} // Ensure theme changes on click
                  />
                  <label htmlFor="flexRadioDefault1">
                    <img
                      className="ml-[1rem] cursor-pointer"
                      width="100"
                      height="68"
                      alt="settings-dark"
                      src={darkLight}
                    />
                  </label>
                </div>

                <div title="Light Mode">
                  <input
                    type="radio"
                    id="flexRadioDefault2"
                    checked={theme === "light"}
                    onChange={toggleTheme} // Ensure theme changes on click
                  />
                  <label htmlFor="flexRadioDefault2">
                    <img
                      className="mx-3 cursor-pointer"
                      width="100"
                      height="68"
                      alt="settings-light"
                      src={settingLight}
                    />
                  </label>
                </div>
              </div>

              <DeactivateAccount />
            </div>
          </div>
        </div>
      </div>

      {toggle && (
        <div className="dark:border-darkBorder rounded-xl border pb-[24px] md:w-[45%] lg:shadow-xl">
          <div className="flex items-center justify-between px-3 pt-4">
            <h2 className="rounded-md bg-[#54d62c29] p-1 text-xs font-bold text-[#229a16]">
              Billzpay
            </h2>

            <MdClose
              size={28}
              onClick={() => {
                setToggle(false);
                setImgFile(null);
                setPreviewImage("");
              }}
              className="cursor-pointer rounded-lg border border-black/70 p-1"
            />
          </div>

          <div className="flex flex-col items-center justify-center">
            <div className="mx-auto size-[144px] rounded-full border border-dashed border-black/85 p-1 dark:border-[#919eab52]">
              <form onSubmit={uploadImage}>
                <input
                  type="file"
                  id="file"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) =>
                    e.target.files && setImgFile(e.target.files[0])
                  }
                />
                <button
                  id="submitBtn"
                  type="submit"
                  className="hidden"
                ></button>
              </form>

              {previewImage ? (
                <img
                  src={previewImage}
                  alt="Preview"
                  className="h-full w-full rounded-full object-cover"
                />
              ) : (
                <div
                  onClick={() => document.querySelector("#file").click()}
                  className="flex h-full w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-full bg-gray-200 hover:bg-gray-300 dark:bg-[#161c24]"
                >
                  <TbCameraPlus
                    size={30}
                    className="text-black dark:text-white"
                  />
                  <p className="text-[12px] font-semibold dark:text-white">
                    Update Photo
                  </p>
                </div>
              )}
            </div>

            <p className="max-w-48 py-2 text-center text-[12px] dark:text-white">
              Allowed *.jpeg, *.jpg, *.png, *.gif max size of 2MB
            </p>

            {imgFile && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => document.querySelector("#file").click()}
                  className="bg-primary rounded-md px-4 py-2.5 text-sm font-medium text-white"
                >
                  Change Image
                </button>

                <button
                  onClick={() => document.querySelector("#submitBtn").click()}
                  className={cn(
                    "bg-primary rounded-md px-4 py-2.5 text-sm font-medium text-white",
                  )}
                >
                  Update
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <form
        className="grid w-full grid-cols-2 gap-6 lg:gap-x-7"
        onSubmit={handleSubmit(handleSave)}
      >
        {/* Full Name */}
        <div className="col-span-2 md:col-span-1">
          <h4 className="pb-1 text-[12px] font-semibold">Full Name</h4>

          <input
            type="text"
            {...register("name")}
            className="dark:border-darkBorder dark:bg-darkInput w-full rounded-md border bg-gray-100 px-4 py-3 outline-none dark:text-gray-400"
          />
        </div>

        <div className="col-span-2 md:col-span-1">
          <h4 className="pb-1 text-[12px] font-semibold">Username</h4>

          <input
            type="text"
            {...register("username")}
            className="dark:border-darkBorder dark:bg-darkInput w-full rounded-md border bg-gray-100 px-4 py-3 text-gray-700 outline-none dark:text-gray-400"
          />
        </div>

        <div className="col-span-2 md:col-span-1">
          <h4 className="pb-1 text-[12px] font-semibold">Email address</h4>

          <input
            type="text"
            {...register("email")}
            className="dark:border-darkBorder dark:bg-darkInput w-full rounded-md border bg-gray-100 px-4 py-3 text-gray-700 outline-none dark:text-gray-400"
            readOnly
          />
        </div>

        <div className="col-span-2 md:col-span-1">
          <h4 className="pb-1 text-[12px] font-semibold">Phone Number</h4>

          <input
            type="text"
            className="dark:border-darkBorder dark:bg-darkInput w-full rounded-md border bg-gray-100 px-4 py-3 outline-none dark:text-gray-400"
            {...register("phone")}
          />
        </div>

        {/* <div className="col-span-2 md:col-span-1">
          <h4 className="pb-1 text-[12px] font-semibold">BVN</h4>

          <input
            type="text"
            // value={"Raythedev"}
            {...register("bvn")}
            className=" dark:border-darkBorder dark:bg-darkInput w-full rounded-md border bg-gray-100 px-4 py-3 outline-none dark:placeholder:text-gray-600"
            // readOnly
            placeholder="Please add your BVN number"
          />
        </div> */}

        <div className="col-span-2">
          <h4 className="pb-1 text-[12px] font-semibold">NIN</h4>

          <input
            type="text"
            {...register("nin")}
            className="dark:border-darkBorder dark:bg-darkInput w-full rounded-md border bg-gray-100 px-4 py-3 outline-none dark:placeholder:text-gray-600"
            // readOnly
            placeholder="Please put in your National ID number"
          />
        </div>

        <div className="col-span-2 md:col-span-2">
          <h4 className="pb-1 text-[12px] font-semibold">Address</h4>

          <textarea
            type="text"
            rows={3}
            name="address"
            {...register("address")}
            // value={"Raythedev"}
            className="dark:border-darkBorder dark:bg-darkInput w-full rounded-md border bg-gray-100 px-4 py-3 outline-none dark:placeholder:text-gray-600"
            // readOnly
            placeholder="Please put in your address details"
          />
        </div>

        <div></div>

        <div className="flex h-fit justify-end">
          <button
            className="bg-primary rounded-md px-7 py-2 font-semibold text-white disabled:opacity-60"
            type="submit"
            disabled={profile.loading}
          >
            {profile.loading ? "Saving" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};
export default ProfileDetails;
